/**
 * Dropdown Menu
 * 
 * @Author: Focci
 * @Date: 2023-05-19 14:46:28
 * @Last Modified by: Focci
 * @Last Modified time: 2023-05-19 14:46:28
 */

import PropTypes from 'prop-types'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import clsx from 'clsx'
import { useCallback, useState } from 'react'
import { ButtonBase, Menu, MenuItem, CircularProgress, Checkbox } from '@comp/material'
import { isArray } from 'lodash-es'

function DropdownMenuTrigger({
  wrapperClassName,
  loading,
  handleClick,
  trigger,
}) {
  return (
    <div className={wrapperClassName}>
      <div
        role="button"
        aria-label="Button"
        tabIndex="0"
        className={clsx([loading && 'invisible', 'h-full'])}
        onClick={handleClick}
        onKeyPress={handleClick}
      >
        {trigger || (
          <ButtonBase>
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </ButtonBase>
        )}
      </div>
      {
        loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress size=".8rem" />
          </div>
        )
      }
    </div>
  )
}

function DropdownMenuItemSimple({
  selected,
  selectedItemHasBorder,
  color,
  label,
  showItemArrow,
  ...props
}) {
  return (
    <MenuItem 
      selected={selected}
      {...props}
      sx={(theme) => ({
        '&.MuiMenuItem-root': {
          fontSize: '.7rem',
          ...(selectedItemHasBorder ? {
            borderLeft: `solid 2px ${selected ? theme.palette[color].main : 'transparent'}` 
          } : null),
          color: selected 
            ? theme.palette[color].main 
            : 'inherit'
        }
      })}
    >
      <div className="flex items-center gap-2">
        {label}
        {showItemArrow && <ArrowForwardIosIcon className="size-2 ml-auto" />}
      </div>
    </MenuItem>
  )
}

function DropdownMenuItemCheckbox({
  selected,
  selectedItemHasBorder,
  label,
  showItemArrow,
  onClick,
  onCheckboxChange = () => {}
}) {
  return (
    <div
      className={clsx([
        'pl-3 hover:bg-cf0 text-f.7 cursor-pointer',
        selected ? 'text-primary bg-primary/10 hover:bg-primary/20 border-l-primary' : 'border-l-transparent',
        selectedItemHasBorder && 'border-l border-l-2'
      ])}
    >
      <div className="flex items-center gap-2">
        <Checkbox checked={selected} onChange={onCheckboxChange} />
        <div
          onClick={onClick}
          onKeyPress={onClick}
          role="button"
          tabIndex={-1}
          className="py-[.3rem] pr-3 flex-auto flex items-center justify-between gap-4"
        >
          {label}
          {showItemArrow && <ArrowForwardIosIcon className="size-2 ml-auto" />}
        </div>
      </div>
    </div>
  )
}

export default function DropdownMenu({
  trigger = null,
  value = null,
  options = [],
  loading = false,
  color = 'primary',
  wrapperClassName = 'relative',
  onChange = () => {},
  onShow = () => {},
  onHide = () => {},
  selectedItemHasBorder = false,
  showCheckbox = false,
  showItemArrow = false,
  ...menuProps
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const targetValue = isArray(value) ? value : [value]

  const handleShow = useCallback((e) => {
    setAnchorEl(e.currentTarget)
    onShow()
  }, [onShow])

  const handleHide = useCallback(() => {
    setAnchorEl(null)
    onHide()
  }, [onHide])

  const handleItemClick = useCallback((opt) => {
    !showCheckbox && handleHide()
    onChange(opt, showCheckbox)
  }, [handleHide, onChange, showCheckbox])

  return (
    <>
      <DropdownMenuTrigger 
        trigger={trigger}
        loading={loading}
        handleClick={handleShow}
        wrapperClassName={wrapperClassName}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleHide}
        PaperProps={{
          style: {
            maxHeight: 500,
            width: 'auto',
          },
        }}
        {...menuProps}
      >
        {options.map((opt) => {
          const selected = targetValue.indexOf(opt.value) > -1
          const Comp = showCheckbox ? DropdownMenuItemCheckbox : DropdownMenuItemSimple

          return (
            <Comp 
              key={opt.value}
              selected={selected}
              selectedItemHasBorder={selectedItemHasBorder}
              showItemArrow={showItemArrow}
              label={opt.label}
              onClick={() => handleItemClick(opt)}
              color={color} 
              onCheckboxChange={() => onChange(opt, true)}
            />
          )
        })}
      </Menu>
    </>
  )
}

DropdownMenu.propTypes = {
  trigger: PropTypes.node,
  value: PropTypes.any,
  options: PropTypes.array,
  loading: PropTypes.bool,
  color: PropTypes.string,
  onChange: PropTypes.func,
}

const DropdownTriggerWithIconProps = {
  label: PropTypes.string,
  icon: PropTypes.node,
}

/**
 * 带Icon和arrow的trigger
 */
export function DropdownTriggerWithIcon({
  label = '',
  icon = null
}) {
  return (
    <ButtonBase aria-label="dropdown">
      <div className="flex items-center gap-x-1 transition-colors hover:bg-cf0 duration-500">
        <span className="text-meta">{icon}</span>
        <span className="text-primary">
          {label}
          <KeyboardArrowDownIcon sx={{ fontSize: '.7rem' }} />
        </span>
      </div>
    </ButtonBase>
  )
}

DropdownTriggerWithIcon.propTypes = DropdownTriggerWithIconProps

export function DropdownMenuWithIcon({
  label = null,
  icon = null,
  ...menuProps
}) {
  return (
    <DropdownMenu
      trigger={<DropdownTriggerWithIcon 
        label={label} 
        icon={icon} 
      />}
      {...menuProps}
    />
  )
}

DropdownMenuWithIcon.propTypes = DropdownTriggerWithIconProps
